/**
 * ProcessRunsTable — every analyzed rollout of the task as a row:
 * model, run id, status, score, turns, and the diagnosed primary
 * failure. Rows whose ordinal id matches an exported rollout open that
 * transcript on click; analysis-only rows render without the action.
 */

import { useState } from 'react'

import { processEntryFor, humanizeTag } from '@/components/benchmarks/goldenProcess'
import { formatScore } from '@/components/evals/format'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { ProcessRollout, RolloutEntry } from '@/api/generated/model'

const ALL = 'all'

export function ProcessRunsTable({
  rollouts,
  entries,
  selected,
  onSelect,
}: {
  rollouts: ProcessRollout[]
  entries: RolloutEntry[]
  selected: { model: string; rolloutId: string } | undefined
  onSelect: (entry: RolloutEntry) => void
}): React.JSX.Element {
  const [modelFilter, setModelFilter] = useState(ALL)
  const [statusFilter, setStatusFilter] = useState(ALL)

  const models = [...new Set(rollouts.map((r) => r.model))]
  const statuses = [...new Set(rollouts.map((r) => r.terminated_reason))]
  const rows = rollouts.filter(
    (r) =>
      (modelFilter === ALL || r.model === modelFilter) &&
      (statusFilter === ALL || r.terminated_reason === statusFilter),
  )
  const entryFor = (rollout: ProcessRollout): RolloutEntry | undefined =>
    processEntryFor(rollout, entries)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Select value={modelFilter} onValueChange={setModelFilter}>
          <SelectTrigger className="w-48" aria-label="Filter by model">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All models</SelectItem>
            {models.map((model) => (
              <SelectItem key={model} value={model}>
                {model}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {humanizeTag(status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              <TableHead>Run</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Score</TableHead>
              <TableHead className="text-right">Turns</TableHead>
              <TableHead>Primary failure</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-sm text-muted-foreground">
                  No runs match the filters.
                </TableCell>
              </TableRow>
            )}
            {rows.map((rollout) => {
              const entry = entryFor(rollout)
              const isSelected =
                selected !== undefined &&
                selected.model === rollout.model &&
                selected.rolloutId === rollout.rollout_id
              return (
                <TableRow
                  key={`${rollout.model}/${rollout.label}`}
                  onClick={entry !== undefined ? () => { onSelect(entry) } : undefined}
                  tabIndex={entry !== undefined ? 0 : undefined}
                  aria-label={
                    entry !== undefined
                      ? `Open run ${rollout.model} ${rollout.rollout_id}`
                      : undefined
                  }
                  onKeyDown={
                    entry !== undefined
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            onSelect(entry)
                          }
                        }
                      : undefined
                  }
                  aria-selected={isSelected || undefined}
                  className={cn(
                    entry !== undefined &&
                      'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isSelected && 'bg-muted',
                  )}
                >
                  <TableCell className="font-mono text-xs">{rollout.model}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {entry !== undefined ? rollout.rollout_id : rollout.label}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {humanizeTag(rollout.terminated_reason)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatScore(rollout.score)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {rollout.n_turns}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {rollout.failure !== null ? humanizeTag(rollout.failure.primary_tag) : ''}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
